import React, { useEffect, useState } from "react";
import axios from "../api/axios";
import {
  Search,
  Monitor,
  Signal,
  Power,
  MapPin,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet"; // Import Leaflet để tạo icon cho Marker
import "leaflet/dist/leaflet.css"; // Import Leaflet CSS

// Component hiển thị thông tin bản đồ
const MapComponent = ({ latitude, longitude, imei }) => {
  const position = [latitude, longitude];

  useEffect(() => {
    const defaultIcon = new L.Icon({
      iconUrl: require("leaflet/dist/images/marker-icon.png"),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });

    L.Marker.prototype.options.icon = defaultIcon;
  }, []);

  return (
    <MapContainer
      center={position}
      zoom={13}
      style={{ height: "100%", width: "100%" }}
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Marker position={position}>
        <Popup>
          <p>
            <strong>IMEI:</strong> {imei}
          </p>
          <p>
            <strong>Tọa độ:</strong> {latitude.toFixed(6)},{" "}
            {longitude.toFixed(6)}
          </p>
        </Popup>
      </Marker>
    </MapContainer>
  );
};

// Component hiển thị thông tin thiết bị
const DeviceTable = ({ devices, search, onOpenMapModal }) => {
  const filteredDevices = devices.filter((device) =>
    device.imei.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <table className="min-w-full divide-y divide-gray-100">
      <thead className="bg-gray-50/50">
        <tr>
          <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">
            Thiết bị (IMEI)
          </th>
          <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">
            Cập nhật lần cuối
          </th>
          <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">
            Vị trí
          </th>
          <th className="px-8 py-5 text-center text-xs font-black text-gray-400 uppercase tracking-widest">
            Tốc độ
          </th>
          <th className="px-8 py-5 text-center text-xs font-black text-gray-400 uppercase tracking-widest">
            Quãng đường
          </th>
          <th className="px-8 py-5 text-center text-xs font-black text-gray-400 uppercase tracking-widest">
            Vệ tinh
          </th>
          <th className="px-8 py-5 text-center text-xs font-black text-gray-400 uppercase tracking-widest">
            Trạng thái
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-50">
        {filteredDevices.map((device) => (
          <tr
            key={device.imei}
            className="hover:bg-blue-50/30 transition-all group"
          >
            <td className="px-8 py-5 whitespace-nowrap">
              <span className="text-base font-mono font-black text-gray-900 bg-white border border-gray-200 px-3 py-1.5 rounded-lg group-hover:border-blue-300 group-hover:text-blue-700 shadow-sm transition-all">
                {device.imei}
              </span>
            </td>
            <td className="px-8 py-5 whitespace-nowrap text-sm text-gray-500 font-medium">
              {new Date(device.gpsdate_unix * 1000).toLocaleString("vi-VN")}
            </td>
            <td className="px-8 py-5 whitespace-nowrap">
              <button
                onClick={() => onOpenMapModal(device)}
                className="flex items-center gap-2 text-xs bg-blue-50 text-blue-700 px-4 py-2 rounded-xl font-black hover:bg-blue-600 hover:text-white transition-all shadow-sm active:scale-95"
              >
                <MapPin size={14} /> {device.latitude.toFixed(6)},{" "}
                {device.longitude.toFixed(6)}
              </button>
            </td>
            <td className="px-8 py-5 whitespace-nowrap text-center">
              <div className="flex flex-col items-center">
                <span className="text-lg font-black text-gray-900 leading-none">
                  {device.speed_kmh}
                </span>
                <span className="text-[10px] text-gray-400 font-black uppercase mt-1">
                  KM/H
                </span>
              </div>
            </td>
            <td className="px-8 py-5 whitespace-nowrap text-center text-sm font-bold text-gray-600">
              {device.mileage}
            </td>
            <td className="px-8 py-5 whitespace-nowrap text-center">
              <div className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full">
                <span className="text-sm font-black text-gray-700">
                  {device.gnss_satellites}
                </span>
                <span className="text-[10px] text-gray-400 font-black uppercase">
                  Sats
                </span>
              </div>
            </td>
            <td className="px-8 py-5 whitespace-nowrap text-center">
              {device.acc_status === "On" ? (
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-700 ring-2 ring-emerald-50">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                  ĐANG CHẠY
                </span>
              ) : (
                <span className="inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-black bg-gray-100 text-gray-400 ring-2 ring-gray-50">
                  <span className="w-2 h-2 bg-gray-300 rounded-full mr-2"></span>
                  NGOẠI TUYẾN
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// Component hiển thị thông tin thống kê (Số lượng thiết bị, trạng thái...)
const StatisticsCard = ({ label, value, icon, bgColor, textColor }) => (
  <div
    className={`bg-white p-8 rounded-3xl shadow-sm border border-gray-100 flex items-center justify-between transition-all hover:shadow-md`}
  >
    <div>
      <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">
        {label}
      </p>
      <p className="text-4xl font-black text-gray-900 mt-2">{value}</p>
    </div>
    <div className={`${bgColor} p-5 rounded-2xl ${textColor}`}>{icon}</div>
  </div>
);

const Realtime = () => {
  const [devices, setDevices] = useState([]);
  const [search, setSearch] = useState("");
  const [totalDevices, setTotalDevices] = useState(0);
  const [onlineDevices, setOnlineDevices] = useState(0);
  const [offlineDevices, setOfflineDevices] = useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [showMapModal, setShowMapModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    fetchRealTimeData();
  }, []);

  const fetchRealTimeData = async () => {
    try {
      setLoading(true);
      const response = await axios.get("./logs/real-time");
      if (response) {
        const data = response.data;
        setDevices(data);
        const total = data.length;
        const online = data.filter(
          (device) => device.acc_status === "On"
        ).length;
        const offline = total - online;

        setTotalDevices(total);
        setOnlineDevices(online);
        setOfflineDevices(offline);
      }
    } catch (error) {
      setErrorMsg("Không thể kết nối API.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleOpenMapModal = (device) => {
    setSelectedDevice(device);
    setShowMapModal(true);
  };

  const handleCloseMapModal = () => {
    setShowMapModal(false);
    setSelectedDevice(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 p-2 md:p-4 lg:p-6">
      <div className="max-w-[98%] mx-auto">
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">
              Hệ Thống Theo Dõi Real-time
            </h1>
            <p className="text-gray-500 text-sm font-medium">
              Quản lý thiết bị GPS trên toàn hệ thống
            </p>
          </div>

          <div className="relative max-w-xl w-full">
            <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400">
              <Search size={20} />
            </span>
            <input
              type="text"
              value={search}
              onChange={handleSearch}
              placeholder="Nhập số IMEI để tìm kiếm nhanh..."
              className="block w-full pl-12 pr-4 py-3.5 border border-gray-200 rounded-2xl leading-5 bg-gray-50/50 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white focus:border-transparent shadow-inner transition-all text-lg font-medium"
            />
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <StatisticsCard
            label="Tổng số thiết bị"
            value={totalDevices}
            icon={<Monitor size={32} />}
            bgColor="bg-blue-50"
            textColor="text-blue-600"
          />
          <StatisticsCard
            label="Đang trực tuyến"
            value={onlineDevices}
            icon={<Signal size={32} />}
            bgColor="bg-emerald-50"
            textColor="text-emerald-600"
          />
          <StatisticsCard
            label="Đang ngoại tuyến"
            value={offlineDevices}
            icon={<Power size={32} />}
            bgColor="bg-rose-50"
            textColor="text-rose-600"
          />
        </div>

        {/* Error Box */}
        {errorMsg && (
          <div className="mb-6 flex items-center gap-4 bg-amber-50 border-l-4 border-amber-400 text-amber-900 p-5 rounded-xl shadow-sm">
            <AlertCircle size={24} className="shrink-0 text-amber-500" />
            <div>
              <p className="text-sm font-bold">Lưu ý hệ thống</p>
              <p className="text-xs font-medium opacity-80">{errorMsg}</p>
            </div>
          </div>
        )}

        {/* Device Table */}
        <div className="bg-white shadow-sm rounded-3xl overflow-hidden border border-gray-200">
          <div className="px-8 py-6 border-b border-gray-100 bg-white flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-8 bg-blue-600 rounded-full shadow-lg shadow-blue-200"></div>
              <div>
                <h2 className="text-xl font-black text-gray-800">
                  Danh Sách Theo Dõi
                </h2>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">
                  Hiển thị {devices.length} kết quả
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchRealTimeData}
                disabled={loading}
                className="flex items-center gap-2 text-sm bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-xl transition-all shadow-xl shadow-gray-200 font-bold disabled:opacity-50 active:scale-95"
              >
                {loading ? (
                  <RefreshCw size={18} className="animate-spin" />
                ) : (
                  <RefreshCw size={18} />
                )}
                LÀM MỚI DỮ LIỆU
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32 text-gray-500 bg-gray-50/30">
                <RefreshCw
                  size={48}
                  className="animate-spin text-blue-500 mb-4 opacity-50"
                />
                <p className="font-black text-gray-400 tracking-widest uppercase text-sm">
                  Đang tải dữ liệu...
                </p>
              </div>
            ) : (
              <DeviceTable
                devices={devices}
                search={search}
                onOpenMapModal={handleOpenMapModal}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal Bản đồ - Full Width */}
      {showMapModal && selectedDevice && (
        <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-lg flex justify-center items-center z-50 p-2 md:p-6 transition-all">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-7xl w-full h-full max-h-[95vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-300">
            <div className="px-10 py-6 border-b border-gray-100 flex justify-between items-center bg-white shrink-0">
              <div className="flex items-center gap-6">
                <div className="p-4 bg-blue-600 rounded-3xl text-white shadow-xl shadow-blue-100">
                  <MapPin size={28} />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                    Giám Sát Vị Trí
                  </h2>
                  <p className="text-sm font-mono text-blue-600 font-black">
                    IMEI THIẾT BỊ: {selectedDevice.imei}
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseMapModal}
                className="text-gray-400 hover:text-rose-600 hover:bg-rose-50 p-3 rounded-full transition-all group"
              >
                <Power
                  size={32}
                  className="group-hover:rotate-90 transition-transform duration-300"
                />
              </button>
            </div>

            <div className="flex-1 bg-gray-100">
              <MapComponent
                latitude={selectedDevice.latitude}
                longitude={selectedDevice.longitude}
                imei={selectedDevice.imei}
              />
            </div>

            <div className="px-10 py-8 bg-white border-t border-gray-50 flex flex-col md:flex-row justify-between items-center gap-6 shrink-0">
              <div className="flex flex-wrap gap-10 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400">
                    <MapPin size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                      Tọa độ GPS
                    </p>
                    <p className="font-black text-gray-800 text-base">
                      {selectedDevice.latitude.toFixed(6)},{" "}
                      {selectedDevice.longitude.toFixed(6)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-gray-50 flex items-center justify-center text-gray-400">
                    <RefreshCw size={18} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">
                      Trạng thái ACC
                    </p>
                    <p
                      className={`font-black text-base ${
                        selectedDevice.acc_status === "On"
                          ? "text-emerald-600"
                          : "text-rose-600"
                      }`}
                    >
                      {selectedDevice.acc_status === "On"
                        ? "ĐANG HOẠT ĐỘNG"
                        : "ĐÃ TẮT MÁY"}
                    </p>
                  </div>
                </div>
              </div>
              <button
                className="w-full md:w-auto px-12 py-4 bg-gray-900 hover:bg-black text-white font-black rounded-2xl shadow-2xl shadow-gray-200 transition-all active:scale-95 text-sm tracking-widest"
                onClick={handleCloseMapModal}
              >
                ĐÓNG CỬA SỔ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Realtime;
